import { useIntl } from 'react-intl';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    type TimeEntryDocType,
    type TimeEntryWarningCode
} from '@ast-data/shared';
import {
    naturalSortComparator,
    GridActionsCellItem,
    type GridColDef,
    type GridRowId
} from '../../lib/dataGrid';
import { useGridRowActions } from '../../components/useGridRowActions';
import ValidatedGridEditCell from '../../components/ValidatedGridEditCell';
import { useValidationMessage } from '../../i18n/validationMessages';
import { TimeEntryFlag } from '../timeTracking/TimeEntryFlag';
import { entryNetMinutes, formatClock } from '../timeTracking/timeMath';
import GridAutocompleteEditCell from '../../components/GridAutocompleteEditCell';
import GridPersonEditCell from '../../components/GridPersonEditCell';
import { timeEditCell } from '../timeTracking/timeEntryTimeCell';
import { type TimeEntryVariant } from '../timeTracking/timeEntryVariant';
import { isRowComplete, fieldCellError } from './fieldTimeEntryRow';
import DeferredActionsCell from '../../components/DeferredActionsCell';

/**
 * Column definitions for a Feldaufnahme day grid (FieldTimeEntryGrid). The
 * `variant` names the first column (Mitarbeiter / Gerät) and decides whether a
 * Pause column is offered — machines take no break.
 */
export function useFieldTimeEntryColumns({
    variant,
    canEdit,
    conflicts,
    warnings,
    nameOptions,
    draftIds,
    onSaveDraft,
    onDiscardDraft,
    onEditRow,
    isEditing,
    onSaveRow,
    onCancelRow,
    onDelete
}: {
    variant: TimeEntryVariant;
    canEdit: boolean;
    conflicts: Set<string>;
    warnings: Map<string, TimeEntryWarningCode[]>;
    nameOptions: string[];
    draftIds: Set<string>;
    onSaveDraft: (id: string) => void;
    onDiscardDraft: (id: string) => void;
    /** Open the row for inline editing (same as tapping one of its cells). */
    onEditRow: (row: TimeEntryDocType) => void;
    isEditing: (id: GridRowId) => boolean;
    /** Commit the open row (the grid rejects it if a cell is invalid). */
    onSaveRow: (id: GridRowId) => void;
    /** Close the open row, discarding its uncommitted edits. */
    onCancelRow: (id: GridRowId) => void;
    onDelete: (row: TimeEntryDocType) => void;
}): GridColDef<TimeEntryDocType>[] {
    const intl = useIntl();
    const rowAction = useGridRowActions();
    const validationMessage = useValidationMessage();

    // The localized message for a cell's content error (non-blocking), or null.
    const cellError = (row: TimeEntryDocType, field: string) => {
        const code = fieldCellError(row, field);
        return code ? validationMessage(code) : null;
    };
    // Tint a cell red (display + edit) when its value is invalid — see the theme's
    // office rule; here it's a class since the native cells don't set `Mui-error`.
    const invalidClass = (row: TimeEntryDocType, field: string) =>
        fieldCellError(row, field) ? 'cell-invalid' : '';

    const columns: GridColDef<TimeEntryDocType>[] = [
        {
            field: 'flags',
            headerName: '',
            width: 44,
            align: 'center',
            sortable: false,
            filterable: false,
            renderCell: ({ row }) => (
                <TimeEntryFlag
                    conflict={conflicts.has(row.id)}
                    codes={warnings.get(row.id) ?? []}
                />
            )
        },
        {
            // The worker — or the Gerät in the Geräte-Zeiterfassung.
            field: 'workerName',
            headerName: intl.formatMessage({ id: variant.nameLabelId }),
            flex: 1,
            minWidth: 120,
            editable: canEdit,
            // The office grid's worker cell, so the two behave identically:
            // a Mitarbeiter is PICKED from the accounts, a Gerät is named.
            renderEditCell: (params) =>
                variant.namesFromAccounts ? (
                    <GridPersonEditCell {...params} day={params.row.date} />
                ) : (
                    <GridAutocompleteEditCell
                        {...params}
                        options={nameOptions}
                    />
                )
        },
        {
            // Geräte only (see the filter at the end): how many of them the row
            // is about — its hours then count once per piece.
            field: 'quantity',
            headerName: intl.formatMessage({ id: 'timeEntry.field.quantity' }),
            type: 'number',
            width: 72,
            editable: canEdit,
            renderEditCell: (params) => <ValidatedGridEditCell {...params} />
        },
        {
            field: 'startTime',
            headerName: intl.formatMessage({ id: 'timeEntry.field.startTime' }),
            // Wide enough for the TimePicker's clock button (the old native time
            // input needed less) — same width as the office grid's Von/Bis.
            width: 110,
            editable: canEdit,
            cellClassName: ({ row }) => invalidClass(row, 'startTime'),
            /**
             * Shared with the office grid; `cellError` is passed because the field
             * flags without blocking the commit (see fieldCellError), where the
             * office gets its error from preProcessEditCellProps.
             */
            renderEditCell: timeEditCell('startTime', (params) =>
                cellError(params.row, 'startTime')
            )
        },
        {
            field: 'endTime',
            headerName: intl.formatMessage({ id: 'timeEntry.field.endTime' }),
            width: 110,
            editable: canEdit,
            cellClassName: ({ row }) => invalidClass(row, 'endTime'),
            renderEditCell: timeEditCell('endTime', (params) =>
                cellError(params.row, 'endTime')
            )
        },
        {
            field: 'pauseMinutes',
            // "Pause", not the form's "Pause (Min.)": the cell reads as a clock
            // time now, even though what is TYPED into it is still minutes.
            headerName: intl.formatMessage({ id: 'timeEntry.col.pause' }),
            type: 'number',
            width: 96,
            // Display only — the raw minutes stay the value, so sorting,
            // filtering and the number editor are untouched.
            valueFormatter: (value) => formatClock(Number(value) || 0),
            editable: canEdit,
            cellClassName: ({ row }) => invalidClass(row, 'pauseMinutes'),
            renderEditCell: (params) => (
                <ValidatedGridEditCell
                    {...params}
                    error={cellError(params.row, 'pauseMinutes')}
                />
            )
        },
        {
            field: 'net',
            headerName: intl.formatMessage({ id: 'timeEntry.field.net' }),
            width: 80,
            sortComparator: naturalSortComparator,
            // Anzahl included — see entryNetMinutes.
            valueGetter: (_value, row) => formatClock(entryNetMinutes(row))
        },
        {
            // Free text, so no validation cell — the plain editor is enough.
            field: 'note',
            headerName: intl.formatMessage({ id: 'timeEntry.field.note' }),
            flex: 1,
            minWidth: 120,
            editable: canEdit
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 84,
            renderCell: (params) => <DeferredActionsCell {...params} />,
            getActions: ({ id, row }) => {
                if (!canEdit) {
                    return [];
                }
                if (draftIds.has(row.id)) {
                    // Draft: Save (only once complete) or discard the local row.
                    return [
                        <GridActionsCellItem
                            key="save"
                            icon={<CheckIcon />}
                            label={intl.formatMessage({
                                id: 'timeEntry.saveRow'
                            })}
                            color="inherit"
                            disabled={!isRowComplete(row)}
                            onClick={() => onSaveDraft(row.id)}
                        />,
                        <GridActionsCellItem
                            key="discard"
                            icon={<DeleteIcon />}
                            label={intl.formatMessage({
                                id: 'timeEntry.discardRow'
                            })}
                            color="inherit"
                            onClick={() => onDiscardDraft(row.id)}
                        />
                    ];
                }
                // Saved row, open for editing: commit or discard the edits — the
                // same Save/Cancel pair the office grid offers.
                if (isEditing(id)) {
                    return [
                        rowAction.save(() => onSaveRow(id)),
                        rowAction.cancel(() => onCancelRow(id))
                    ];
                }
                // Saved row: open it for inline editing, or delete it.
                return [
                    rowAction.edit(() => onEditRow(row)),
                    rowAction.remove(() => onDelete(row))
                ];
            }
        }
    ];

    // Each variant drops what it has no use for — machines take no break (their
    // rows keep a pauseMinutes of 0, and net = the full span), a person is never
    // counted (a worker row's quantity stays 1).
    const dropped = new Set<string>();
    if (!variant.showPause) {
        dropped.add('pauseMinutes');
    }
    if (!variant.showQuantity) {
        dropped.add('quantity');
    }
    // Read-only (a Mitarbeiter, who records their hours but does not correct
    // them): the actions column would only be an empty 84px gutter.
    if (!canEdit) {
        dropped.add('actions');
    }
    return columns.filter((column) => !dropped.has(column.field));
}
